// "Worth a look" on Summary: plain sentences built from the reports, each with
// one action. Pure functions, so the wording is easy to test. A rule returns
// null when there is nothing worth pointing at.
import { isMaterial, listText, money, pct, signedMoney } from "./format";

const plural = (value, one, many = `${one}s`) => (Number(value) === 1 ? one : many);
const number = (value) => Number(value || 0);
const count = (value) => new Intl.NumberFormat("en-US").format(number(value));

export function customersHighlight(health) {
  const summary = health?.summary;
  if (!summary?.regular_customers) return null;
  const flagged = number(summary.flagged);
  if (!flagged) return null;
  return {
    key: "customers",
    text: `${count(flagged)} ${plural(flagged, "customer has", "customers have")} slipped from how they usually order, worth ${money(summary.at_risk_weekly)} a week.`,
    action: { label: "Open the call list", tab: "customers" },
  };
}

export function categoriesHighlight(report, health) {
  if (!report?.comparison) return null;
  const rows = (report.breakdowns?.product_category || []).filter((row) => row.key !== "adjustment");
  const worst = rows.filter((row) => number(row.change_amount) < 0 && isMaterial(row.change_amount, row.change_percent))
    .sort((a, b) => number(a.change_amount) - number(b.change_amount))[0];
  if (!worst) return null;
  const best = [...rows].sort((a, b) => number(b.change_amount) - number(a.change_amount))[0];
  let text = `${worst.name} fell ${money(-number(worst.change_amount))}${worst.change_percent != null ? ` (${pct(Math.abs(number(worst.change_percent)))})` : ""}`;
  text += best && number(best.change_amount) > 0 ? ` while ${best.name} grew ${money(best.change_amount)}.` : ".";
  const stopped = (health?.rows || []).filter((row) =>
    (row.dropped_categories || []).some((item) => item.id != null && item.id === worst.id)).length;
  if (stopped) text += ` ${count(stopped)} regular ${plural(stopped, "customer")} stopped or cut back on it.`;
  return { key: "categories", text, action: { label: "See categories", breakdown: "product_category", movement: "down" } };
}

const isOffice = (row) => row.is_office || String(row.key || "").endsWith(":unassigned");

export function salesmanHighlight(report, scorecard) {
  if (report?.comparison) {
    const reps = (report.breakdowns?.salesman || []).filter((row) => row.key !== "adjustment" && !isOffice(row));
    const dropped = reps.filter((row) => number(row.change_amount) < 0 && isMaterial(row.change_amount, row.change_percent))
      .sort((a, b) => number(a.change_amount) - number(b.change_amount));
    if (dropped.length) {
      const rep = dropped[0];
      let text = `${rep.name}’s sales fell ${money(-number(rep.change_amount))}${rep.change_percent != null ? ` (${pct(Math.abs(number(rep.change_percent)))})` : ""}.`;
      if (reps.length > 1 && reps.every((row) => row === rep || number(row.change_amount) >= 0)) text += " The other salesmen are flat or up.";
      return { key: "salesman", text, action: { label: `Open ${rep.name}`, rep: rep.key } };
    }
  }
  const flagged = (scorecard?.rows || []).filter((row) => !row.is_office && row.flags?.includes("losing_customers"));
  if (flagged.length) {
    const rep = flagged[0];
    return { key: "salesman",
      text: `${rep.name} kept ${pct(rep.retention_percent, 0)} of their customers from the comparison dates.`,
      action: { label: `Open ${rep.name}`, rep: rep.key } };
  }
  return null;
}

export function accountTypesHighlight(report) {
  if (!report?.comparison) return null;
  const down = (report.breakdowns?.account_type || []).filter((row) => row.key !== "adjustment"
    && number(row.change_amount) < 0 && isMaterial(row.change_amount, row.change_percent))
    .sort((a, b) => number(a.change_amount) - number(b.change_amount)).slice(0, 2);
  if (!down.length) return null;
  const parts = down.map((row) => `${row.name} customers spent ${money(-number(row.change_amount))} less${row.change_percent != null ? ` (${pct(Math.abs(number(row.change_percent)))})` : ""}`);
  return { key: "account_types", text: `${listText(parts)}.`, action: { label: "See account types", breakdown: "account_type", movement: "down" } };
}

/** Biggest growth worth mentioning when nothing else is, so the list is never just bad news. */
export function growthHighlight(report) {
  if (!report?.comparison) return null;
  const rows = (report.breakdowns?.customer || []).filter((row) => row.key !== "adjustment" && number(row.change_amount) > 0);
  const grew = rows.filter((row) => isMaterial(row.change_amount, row.change_percent)).length;
  if (!grew) return null;
  const top = [...rows].sort((a, b) => number(b.change_amount) - number(a.change_amount))[0];
  return { key: "growth", text: `${count(grew)} ${plural(grew, "customer")} grew a lot; ${top.name} most (${signedMoney(top.change_amount)}).`,
    action: { label: "See customers who grew", breakdown: "customer", movement: "up" } };
}
